import { DateRange } from '../models/CustomerStats';
import {
  formatSalesDayDescription,
  matchesSalesDayRange,
  resolveSalesDayRange,
} from './salesDay';

export class DateUtils {
  // Obtenir la date du début de la journée
  static getStartOfDay(date: Date): Date {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
  }

  // Obtenir la date de fin de la journée
  static getEndOfDay(date: Date): Date {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  }

  // Obtenir la plage de dates pour aujourd'hui
  static getTodayRange(): DateRange {
    const today = new Date();
    return {
      start: this.getStartOfDay(today),
      end: this.getEndOfDay(today)
    };
  }

  /**
   * Restaurant sales day on the browser's local clock: 06:00 inclusive until
   * 06:00 the next day exclusive. DateRange is inclusive throughout this app,
   * so the stored end is the millisecond immediately before the next 06:00.
   */
  static getSalesDayRange(now: Date = new Date()): DateRange {
    return resolveSalesDayRange(now);
  }

  // Obtenir la plage de dates pour hier
  static getYesterdayRange(): DateRange {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      start: this.getStartOfDay(yesterday),
      end: this.getEndOfDay(yesterday)
    };
  }

  // Obtenir la plage de dates pour cette semaine
  static getThisWeekRange(): DateRange {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Lundi
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Dimanche
    
    return {
      start: this.getStartOfDay(startOfWeek),
      end: this.getEndOfDay(endOfWeek)
    };
  }

  // Obtenir la plage de dates pour ce mois
  static getThisMonthRange(): DateRange {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    return {
      start: this.getStartOfDay(startOfMonth),
      end: this.getEndOfDay(endOfMonth)
    };
  }

  // Obtenir la plage de dates pour cette année
  static getThisYearRange(): DateRange {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    
    return {
      start: this.getStartOfDay(startOfYear),
      end: this.getEndOfDay(endOfYear)
    };
  }

  // Obtenir la plage de dates pour les 7 derniers jours
  static getLast7DaysRange(): DateRange {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    return {
      start: this.getStartOfDay(sevenDaysAgo),
      end: this.getEndOfDay(today)
    };
  }

  // Obtenir la plage de dates pour les 30 derniers jours
  static getLast30DaysRange(): DateRange {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    return {
      start: this.getStartOfDay(thirtyDaysAgo),
      end: this.getEndOfDay(today)
    };
  }

  // Obtenir la plage de dates pour le mois dernier
  static getLastMonthRange(): DateRange {
    const today = new Date();
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    
    return {
      start: this.getStartOfDay(startOfLastMonth),
      end: this.getEndOfDay(endOfLastMonth)
    };
  }

  // Formater une date en français
  static formatDateFR(date: Date): string {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Formater une date et heure en français
  static formatDateTimeFR(date: Date): string {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Obtenir le nom du jour en français
  static getDayNameFR(date: Date): string {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[date.getDay()];
  }

  // Obtenir le nom du mois en français
  static getMonthNameFR(date: Date): string {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[date.getMonth()];
  }

  // Calculer la différence en jours entre deux dates
  static getDaysDifference(date1: Date, date2: Date): number {
    const timeDiff = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  // Vérifier si une date est dans une plage
  static isDateInRange(date: Date, range: DateRange): boolean {
    return date >= range.start && date <= range.end;
  }

  // Obtenir les options de plage de dates prédéfinies
  static getDateRangeOptions(options: { includeSalesDay?: boolean } = {}): Array<{ label: string; value: string; range: DateRange }> {
    const ranges = [
      {
        label: "Aujourd'hui",
        value: 'today',
        range: this.getTodayRange()
      },
      {
        label: 'Hier',
        value: 'yesterday',
        range: this.getYesterdayRange()
      },
      {
        label: 'Cette semaine',
        value: 'thisWeek',
        range: this.getThisWeekRange()
      },
      {
        label: 'Ce mois',
        value: 'thisMonth',
        range: this.getThisMonthRange()
      },
      {
        label: 'Cette année',
        value: 'thisYear',
        range: this.getThisYearRange()
      },
      {
        label: '7 derniers jours',
        value: 'last7Days',
        range: this.getLast7DaysRange()
      },
      {
        label: '30 derniers jours',
        value: 'last30Days',
        range: this.getLast30DaysRange()
      },
      {
        label: 'Mois dernier',
        value: 'lastMonth',
        range: this.getLastMonthRange()
      }
    ];
    if (options.includeSalesDay) {
      ranges.splice(1, 0, {
        label: 'Journée de vente · 06:00–06:00',
        value: 'salesDay',
        range: this.getSalesDayRange()
      });
    }
    return ranges;
  }

  // Créer une plage de dates personnalisée
  static createCustomRange(startDate: Date, endDate: Date): DateRange {
    return {
      start: this.getStartOfDay(startDate),
      end: this.getEndOfDay(endDate)
    };
  }

  // Créer une plage personnalisée en conservant les heures choisies.
  // Le filtre est inclusif : la minute de fin est prise entièrement en compte.
  static createCustomDateTimeRange(startDate: Date, endDate: Date): DateRange {
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setSeconds(0, 0);
    end.setSeconds(59, 999);

    return { start, end };
  }

  // Les agrégats journaliers ne sont exacts que pour des journées complètes.
  static isFullDayRange(range: DateRange): boolean {
    return (
      range.start.getHours() === 0 &&
      range.start.getMinutes() === 0 &&
      range.start.getSeconds() === 0 &&
      range.start.getMilliseconds() === 0 &&
      range.end.getHours() === 23 &&
      range.end.getMinutes() === 59 &&
      range.end.getSeconds() === 59 &&
      range.end.getMilliseconds() === 999
    );
  }

  // Valider une plage de dates
  static validateDateRange(range: DateRange): { isValid: boolean; error?: string } {
    if (Number.isNaN(range.start.getTime()) || Number.isNaN(range.end.getTime())) {
      return {
        isValid: false,
        error: 'La date ou l\'heure saisie est invalide'
      };
    }

    if (range.start > range.end) {
      return {
        isValid: false,
        error: 'Le début doit être antérieur à la fin'
      };
    }

    const now = new Date();
    if (range.start > now) {
      return {
        isValid: false,
        error: 'La date de début ne peut pas être dans le futur'
      };
    }

    // Vérifier que la plage n'est pas trop large (plus de 2 ans)
    const daysDiff = this.getDaysDifference(range.start, range.end);
    if (daysDiff > 730) {
      return {
        isValid: false,
        error: 'La plage de dates ne peut pas dépasser 2 ans'
      };
    }

    return { isValid: true };
  }

  // Obtenir une description textuelle d'une plage de dates
  static getDateRangeDescription(range: DateRange): string {
    const startStr = this.formatDateFR(range.start);
    const endStr = this.formatDateFR(range.end);
    
    if (startStr === endStr) {
      return `Le ${startStr}`;
    }
    
    return `Du ${startStr} au ${endStr}`;
  }

  static getDateTimeRangeDescription(range: DateRange): string {
    if (this.isSalesDayRange(range)) {
      return formatSalesDayDescription(range);
    }
    const startDate = this.formatDateFR(range.start);
    const endDate = this.formatDateFR(range.end);
    const startTime = range.start.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
    const endTime = range.end.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });

    if (startDate === endDate) {
      return `Le ${startDate}, de ${startTime} à ${endTime}`;
    }

    return `Du ${startDate} à ${startTime} au ${endDate} à ${endTime}`;
  }

  static isSalesDayRange(range: DateRange): boolean {
    return matchesSalesDayRange(range);
  }

  // Obtenir les heures de pointe (analyse des patterns temporels)
  static getPeakHours(hourDistribution: number[]): { hour: number; count: number }[] {
    return hourDistribution
      .map((count, hour) => ({ hour, count }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }

  // Obtenir les jours les plus actifs
  static getPeakDays(dayDistribution: number[]): { day: number; dayName: string; count: number }[] {
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    
    return dayDistribution
      .map((count, day) => ({ day, dayName: dayNames[day], count }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }
}

export default DateUtils;
